import React, { useState, useEffect } from 'react';
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { getApiUrl } from "../../config/api";

interface UnderConstructionData {
  status: number;
  title?: string;
  message?: string;
  expectedDate?: string;
}

const UnderConstruction = () => {
  const [data, setData] = useState<UnderConstructionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        console.log('Checking under construction status...');
        
        // Determine the correct API URL based on environment
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const apiUrl = isDevelopment 
          ? getApiUrl('STATUS')
          : getApiUrl('STATUS');
        
        console.log('Using API URL:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors' // Explicitly set CORS mode
        });
        
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('API result:', result);
        
        if (result.status === 1) {
          console.log('Status is 1, showing maintenance page');
          setData(result);
        } else {
          console.log('Status is not 1, redirecting to home');
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error checking status:', error);
        
        // If it's a CORS error in development, show a helpful message
        if (error instanceof Error && error.message.includes('Failed to fetch')) {
          setError('Erreur CORS: Impossible d\'accéder à l\'API depuis localhost. Veuillez tester sur le serveur de production.');
        } else {
          setError(error instanceof Error ? error.message : 'Unknown error');
        }
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification du statut de maintenance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong>Erreur:</strong> {error}
          </div>
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            <strong>Solution:</strong> Testez cette page sur le serveur de production (www.kissariti.ma) plutôt que localhost.
          </div>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-pink text-white px-6 py-2 rounded-lg hover:bg-pink-600 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.status !== 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Redirection vers l'accueil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-pink-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-purple-200 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-1/3 w-20 h-20 bg-blue-200 rounded-full opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo/Brand */}
          <div className="mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-pink to-purple rounded-full shadow-lg mb-6">
              <Icon.Hammer size={40} className="text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-pink via-purple to-blue bg-clip-text text-transparent">
              Kissarity
            </h1>
          </div>

          {/* Main Message */}
          <div className="mb-12 animate-fade-in" style={{animationDelay: '0.2s'}}>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">
              {data.title || "Site en maintenance"}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {data.message || "Nous effectuons actuellement des travaux de maintenance pour améliorer votre expérience. Merci de votre patience !"}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8 animate-fade-in" style={{animationDelay: '0.4s'}}>
            <div className="w-full max-w-md mx-auto bg-white rounded-full h-3 shadow-inner overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink to-purple rounded-full animate-progress"></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">Maintenance en cours...</p>
          </div>

          {/* Features Preview */}
          <div className="mb-12 animate-fade-in" style={{animationDelay: '0.6s'}}>
            <h3 className="text-2xl font-bold text-gray-800 mb-8">
              Améliorations en cours...
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                {
                  icon: Icon.ShoppingBag,
                  title: "Boutique améliorée",
                  description: "Interface plus rapide et plus intuitive"
                },
                {
                  icon: Icon.Truck,
                  title: "Performance améliorée",
                  description: "Vitesse de chargement optimisée"
                },
                {
                  icon: Icon.Shield,
                  title: "Sécurité renforcée",
                  description: "Protection accrue de vos données"
                }
              ].map((feature, index) => (
                <div
                  key={feature.title}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-fade-in"
                  style={{animationDelay: `${0.8 + index * 0.2}s`}}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-pink to-purple rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <feature.icon size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="animate-fade-in" style={{animationDelay: '1.4s'}}>
            <p className="text-gray-500 text-sm">
              © 2024 Kissarity. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Construction Elements */}
      <div className="absolute top-10 right-10 w-16 h-16 opacity-10 animate-spin" style={{animationDuration: '20s'}}>
        <Icon.Wrench size={64} className="text-gray-400" />
      </div>

      <div className="absolute bottom-10 left-10 w-12 h-12 opacity-10 animate-bounce">
        <Icon.Screwdriver size={48} className="text-gray-400" />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes progress {
            from { width: 0%; }
            to { width: 75%; }
          }
          
          .animate-fade-in {
            animation: fade-in 0.8s ease-out forwards;
            opacity: 0;
          }
          
          .animate-progress {
            animation: progress 2s ease-out forwards;
          }
        `
      }} />
    </div>
  );
};

export default UnderConstruction; 